/**
 * The check-in terms variable map: context in, `{{path}}` values out.
 *
 * ### Why the context is not a session row
 *
 * This function runs on both sides of the two-zone split. `sm4rt-public-api`
 * builds the context from a Dataverse row and an app-settings key; the admin
 * editor builds one from sample data; the check-in page builds one from the
 * public session payload. None of those shapes match, and forcing them to
 * would drag Dataverse column names into the browser.
 *
 * So the seam is here, at an already-normalised context: dates are Sydney
 * calendar dates (`YYYY-MM-DD`), money is a number, and every caller does its
 * own mapping to reach this shape. What must match across languages is only
 * what happens *after* — the formatting and the fallbacks, which is exactly
 * what `fixtures/terms-template-rendering.json` pins.
 *
 * The Python twin is `core/services/template_variables.py` in
 * `sm4rt-public-api`. Change one, change both, and add a fixture case.
 */

import {
  CHECKIN_TEMPLATE_VARIABLES,
  type CheckInTermsIntakeType,
} from '../contracts/checkInTermsTemplate';
import {
  BUSINESS_HOURS_DEFAULT,
  type BusinessHoursConfig,
} from '../contracts/timeWindow';
import {
  businessHoursValues,
  formatCurrencyAud,
  formatLongDate,
} from './formatters';
import type { TemplateValues } from './templateRender';

/**
 * Everything a check-in terms body may reference, already normalised.
 *
 * Bounded by what `sm4rt-public-api` is allowed to read (its background.md
 * §6.2). Adding a field here that the public app cannot fetch produces a
 * variable that resolves in the preview and renders empty for the customer —
 * worse than not offering it.
 */
export interface CheckInTemplateContext {
  sessionNumber: string | null;
  intakeType: CheckInTermsIntakeType | null;
  /** Live charges total. Null and 0 both render "$0.00". */
  amountDue: number | null;
  /** Sydney calendar date, `YYYY-MM-DD`. */
  expiresAt: string | null;
  termsVersion: string | null;
  customerName: string | null;
  businessName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  companyName: string | null;
  /** Sydney calendar date, `YYYY-MM-DD`. */
  today: string;
  /** Null falls back to {@link BUSINESS_HOURS_DEFAULT}, as quote terms do. */
  businessHours: BusinessHoursConfig | null;
  returnGraceDays: number | null;
}

const DEFAULT_COMPANY_NAME = 'Tech Solvers';

const INTAKE_TYPE_LABELS: Record<CheckInTermsIntakeType, string> = {
  in_store: 'In store',
  online: 'Online',
};

/** Fallbacks declared in the registry, applied to any value that came up empty. */
const FALLBACKS: TemplateValues = Object.fromEntries(
  CHECKIN_TEMPLATE_VARIABLES.filter((v) => v.fallback !== undefined).map((v) => [
    v.path,
    v.fallback as string,
  ]),
);

const text = (value: string | null | undefined): string => (value ?? '').trim();

export function buildCheckInTemplateValues(
  ctx: CheckInTemplateContext,
): TemplateValues {
  const hours = ctx.businessHours ?? BUSINESS_HOURS_DEFAULT;

  const values: TemplateValues = {
    ...businessHoursValues(hours),
    returnGraceDays:
      ctx.returnGraceDays != null ? String(ctx.returnGraceDays) : '',

    'session.number': text(ctx.sessionNumber),
    'session.intakeType': ctx.intakeType
      ? INTAKE_TYPE_LABELS[ctx.intakeType] ?? ''
      : '',
    // Null means "no charges row yet", not "unknown": a check-in with nothing
    // billable owes nothing, and "$0.00" is the honest rendering of that.
    'session.amountDue': formatCurrencyAud(ctx.amountDue),
    'session.expiresAt': formatLongDate(ctx.expiresAt),
    'terms.version': text(ctx.termsVersion),

    'customer.name': text(ctx.customerName),
    'customer.businessName': text(ctx.businessName),
    'customer.email': text(ctx.customerEmail),
    'customer.phone': text(ctx.customerPhone),

    'system.companyName': text(ctx.companyName) || DEFAULT_COMPANY_NAME,
    'system.today': formatLongDate(ctx.today),
  };

  // The registry owns the fallbacks so both languages read them from one list
  // rather than each hard-coding "you" somewhere in a formatter.
  for (const [path, fallback] of Object.entries(FALLBACKS)) {
    if (!values[path] || values[path].trim() === '') {
      values[path] = fallback;
    }
  }

  return values;
}
